import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";

const SOURCE_LABELS = Object.freeze({
  prevision: "Prevision",
  sienge: "Sienge",
  mega: "MEGA",
  none: "Sem fonte",
});

const CONDITION_LABELS = Object.freeze({
  normal: "Sincronização válida",
  fallback_last_valid: "Último snapshot válido",
  fallback_erp: "Fallback do ERP",
  normal_erp: "Fonte atual do ERP",
  unavailable: "Indisponível",
});

/**
 * Estados em que a curva não veio da sincronização corrente. Recolher a faixa não pode
 * virar esconder: nesses casos a condição fica no próprio gatilho, sem exigir um clique
 * para o usuário descobrir que está olhando um dado de fallback.
 */
const CONDITIONS_NEEDING_ATTENTION = new Set([
  "fallback_last_valid",
  "fallback_erp",
  "unavailable",
]);

const SYNC_LABELS = Object.freeze({
  success: "Sincronizado",
  unchanged: "Sem alteração",
  error_transient: "Falha temporária",
  error_permanent: "Falha de configuração",
  partial_failure: "Falha parcial",
  never_synced: "Nunca sincronizado",
});

const COVERAGE_LABELS = Object.freeze({
  complete: "completa",
  partial: "parcial",
  explicit_zero: "zero confirmado",
  unavailable: "indisponível",
});

const REASON_LABELS = Object.freeze({
  baseline_snapshot_missing: "O snapshot Prevision ainda não está disponível.",
  baseline_snapshot_invalid: "O snapshot Prevision não passou pela validação de identidade.",
  schedule_snapshot_incomplete: "A cobertura por nó está incompleta; o ERP não foi misturado.",
  node_not_covered: "Este nó não possui correspondência no snapshot Prevision.",
  invalid_node_schedule_snapshot: "A curva deste nó não passou pela validação.",
  schedule_budgeted_unit_uncovered: "Há unidades orçadas sem correspondência canônica no Prevision. "
    + "Corrija as células no Prevision ou acione o suporte para definir uma regra de agregação; "
    + "nenhuma curva foi replicada automaticamente.",
  schedule_capture_failed: "O cronograma Prevision não pôde ser processado. "
    + "Tente sincronizar novamente e, se a falha persistir, acione o suporte.",
});

const REASON_TRIGGER_LABELS = Object.freeze({
  schedule_budgeted_unit_uncovered: "Cobertura Prevision incompleta",
  schedule_capture_failed: "Falha no cronograma Prevision",
});

const formatCompetence = (value) => {
  const match = String(value ?? "").match(/^(\d{4})-(\d{2})$/);
  return match ? `${match[2]}/${match[1]}` : null;
};

const formatSyncedAt = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleString("pt-BR");
};

const formatCoverage = (coverage) => {
  if (!coverage || typeof coverage !== "object") return null;
  if (
    typeof coverage.base === "string"
    || typeof coverage.expected === "string"
    || typeof coverage.realized === "string"
  ) {
    return [
      ["Linha de base", coverage.base],
      ["Previsto", coverage.expected],
      ["Medido", coverage.realized],
    ]
      .filter(([, value]) => typeof value === "string")
      .map(([label, value]) => `${label}: ${COVERAGE_LABELS[value] ?? "não informada"}`)
      .join(" · ");
  }
  if (Number.isInteger(coverage.source_rows) && Number.isInteger(coverage.mapped_rows)) {
    const unmapped = Number.isInteger(coverage.unmapped_rows)
      ? coverage.unmapped_rows
      : null;
    return `${coverage.mapped_rows}/${coverage.source_rows} linhas mapeadas${
      unmapped === null ? "" : ` · ${unmapped} sem vínculo`
    }`;
  }
  return null;
};

// Fechar com um pequeno atraso deixa o mouse atravessar do ícone para o detalhe.
const CLOSE_DELAY_MS = 150;

/**
 * Procedência das curvas de planejamento como informação do bloco (revisão de 2026-10-07).
 *
 * A informação é de auditoria: importa quando alguém questiona um número, e não a cada
 * vez que a tela abre. Em vez de um chip "Fonte" no topo, é um ícone "i" no canto inferior
 * direito do bloco que a usa (o pai é `.info-anchor`), e o detalhe abre ao passar o mouse
 * ou com foco, por cima, sem empurrar o conteúdo. Quando a curva não veio da sincronização
 * corrente, o ícone fica em atenção e o nome acessível diz a condição.
 */
const ScheduleSourceStatus = ({ metadata }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const show = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const hide = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  useEffect(() => () => clearTimeout(closeTimer.current), []);
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  if (!metadata || typeof metadata !== "object") return null;
  const source = SOURCE_LABELS[metadata.source] ?? "Sem fonte";
  const condition = CONDITION_LABELS[metadata.condition] ?? "Estado não informado";
  const competence = formatCompetence(metadata.competence);
  const syncedAt = formatSyncedAt(metadata.syncedAt);
  const coverage = formatCoverage(metadata.coverage);
  const reason = REASON_LABELS[metadata.reason] ?? null;
  const reasonTrigger = REASON_TRIGGER_LABELS[metadata.reason] ?? null;
  const syncStatus = SYNC_LABELS[metadata.syncStatus] ?? null;
  const needsAttention = CONDITIONS_NEEDING_ATTENTION.has(metadata.condition)
    || Boolean(reasonTrigger);
  const summary = needsAttention ? `${source}, ${reasonTrigger ?? condition}` : source;

  return (
    <div
      className="chart-info schedule-source-info"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) hide();
      }}
    >
      <button
        type="button"
        className={`chart-info__trigger schedule-source-info__trigger--${metadata.condition ?? "unknown"}${needsAttention ? " chart-info__trigger--attention" : ""}`}
        aria-label={`Fonte das curvas de planejamento: ${summary}. Ver detalhamento`}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden="true">i</span>
      </button>
      {open ? (
        <section
          className="schedule-source-popover chart-info__detail"
          role="dialog"
          aria-label="Detalhes da fonte das curvas de planejamento"
        >
          <span className="coverage-eyebrow">Fonte das curvas de planejamento</span>
          <div className="schedule-source-popover__headline">
            <strong>{source}</strong>
            <span>{condition}</span>
          </div>
          <dl className="schedule-source-popover__details">
            {syncStatus ? <div><dt>Estado</dt><dd>{syncStatus}</dd></div> : null}
            {competence ? <div><dt>Competência</dt><dd>{competence}</dd></div> : null}
            {syncedAt ? <div><dt>Sincronizado em</dt><dd>{syncedAt}</dd></div> : null}
            {coverage ? <div><dt>Cobertura</dt><dd>{coverage}</dd></div> : null}
          </dl>
          {reason ? <p>{reason}</p> : null}
        </section>
      ) : null}
    </div>
  );
};

ScheduleSourceStatus.propTypes = {
  metadata: PropTypes.shape({
    source: PropTypes.string,
    condition: PropTypes.string,
    syncStatus: PropTypes.string,
    competence: PropTypes.string,
    syncedAt: PropTypes.string,
    reason: PropTypes.string,
    coverage: PropTypes.object,
  }),
};

ScheduleSourceStatus.defaultProps = {
  metadata: null,
};

export default ScheduleSourceStatus;
